import React, { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

function toISO(d) { return d.toISOString().split('T')[0] }

var MONTH_NAMES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

export default function ScheduleSettings() {
  var [settings, setSettings] = useState(null)
  var [loading, setLoading] = useState(true)
  var [newBlockDate, setNewBlockDate] = useState('')
  var [saved, setSaved] = useState(false)
  var [overrideYear, setOverrideYear] = useState(new Date().getFullYear())

  useEffect(function() {
    var fetchSettings = async function() {
      var { data } = await supabase.from('schedule_settings').select('*').eq('business_id', 'pools').single()
      if (data) setSettings(data)
      else {
        var { data: created } = await supabase.from('schedule_settings').insert({ business_id: 'pools', max_installs_per_day: 3, blocked_dates: [], monthly_overrides: {} }).select().single()
        setSettings(created)
      }
      setLoading(false)
    }
    fetchSettings()
  }, [])

  var updateSetting = async function(updates) {
    if (!settings) return
    await supabase.from('schedule_settings').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', settings.id)
    setSettings(function(prev) { return { ...prev, ...updates } })
    setSaved(true)
    setTimeout(function() { setSaved(false) }, 2000)
  }

  var addBlockedDate = function() {
    if (!newBlockDate) return
    var current = settings.blocked_dates || []
    if (current.includes(newBlockDate)) return
    var updated = [...current, newBlockDate].sort()
    updateSetting({ blocked_dates: updated })
    setNewBlockDate('')
  }

  var removeBlockedDate = function(date) {
    var updated = (settings.blocked_dates || []).filter(function(d) { return d !== date })
    updateSetting({ blocked_dates: updated })
  }

  var setMonthOverride = function(month, value) {
    var overrides = { ...(settings.monthly_overrides || {}) }
    var key = overrideYear + '-' + String(month + 1).padStart(2, '0')
    if (value === '' || value === null || parseInt(value) === settings.max_installs_per_day) {
      delete overrides[key]
    } else {
      overrides[key] = parseInt(value) || 0
    }
    updateSetting({ monthly_overrides: overrides })
  }

  var getMonthOverride = function(month) {
    var key = overrideYear + '-' + String(month + 1).padStart(2, '0')
    var overrides = settings.monthly_overrides || {}
    return overrides[key] !== undefined ? overrides[key] : null
  }

  if (loading) return <div style={{ color: 'rgba(255,255,255,0.3)', padding: '20px' }}>Loading...</div>

  return (
    <div style={s.container}>
      <div style={s.sectionTitle}>POOL SCHEDULE SETTINGS</div>
      {saved && <div style={s.saved}>Settings saved!</div>}

      {/* Default max per day */}
      <div style={s.settingRow}>
        <div>
          <div style={s.settingLabel}>DEFAULT MAX INSTALLS PER DAY</div>
          <div style={s.settingDesc}>Base limit for all months. Override specific months below.</div>
        </div>
        <div style={s.counterRow}>
          <button onClick={function() { if (settings.max_installs_per_day > 1) updateSetting({ max_installs_per_day: settings.max_installs_per_day - 1 }) }} style={s.counterBtn}>-</button>
          <div style={s.counterValue}>{settings.max_installs_per_day}</div>
          <button onClick={function() { updateSetting({ max_installs_per_day: settings.max_installs_per_day + 1 }) }} style={s.counterBtn}>+</button>
        </div>
      </div>

      {/* Monthly overrides */}
      <div style={{ marginTop: '24px' }}>
        <div style={s.settingLabel}>MONTHLY CAPACITY OVERRIDES</div>
        <div style={s.settingDesc}>Set different slot limits for specific months. Leave blank to use the default ({settings.max_installs_per_day}/day).</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px', marginBottom: '12px' }}>
          <button onClick={function() { setOverrideYear(overrideYear - 1) }} style={s.yearBtn}>{String.fromCodePoint(0x2190)}</button>
          <div style={s.yearLabel}>{overrideYear}</div>
          <button onClick={function() { setOverrideYear(overrideYear + 1) }} style={s.yearBtn}>{String.fromCodePoint(0x2192)}</button>
        </div>

        <div style={s.monthGrid}>
          {MONTH_NAMES.map(function(name, i) {
            var override = getMonthOverride(i)
            var hasOverride = override !== null
            var isPast = overrideYear < new Date().getFullYear() || (overrideYear === new Date().getFullYear() && i < new Date().getMonth())
            return (
              <div key={i} style={{ ...s.monthCard, opacity: isPast ? 0.4 : 1, borderColor: hasOverride ? 'rgba(180,140,60,0.4)' : 'rgba(255,255,255,0.06)' }}>
                <div style={s.monthName}>{name}</div>
                <div style={s.monthInputRow}>
                  <button onClick={function() {
                    var current = override !== null ? override : settings.max_installs_per_day
                    if (current > 0) setMonthOverride(i, current - 1)
                  }} style={s.monthBtn}>-</button>
                  <div style={{ ...s.monthValue, color: hasOverride ? '#b48c3c' : 'rgba(255,255,255,0.5)' }}>
                    {override !== null ? override : settings.max_installs_per_day}
                  </div>
                  <button onClick={function() {
                    var current = override !== null ? override : settings.max_installs_per_day
                    setMonthOverride(i, current + 1)
                  }} style={s.monthBtn}>+</button>
                </div>
                {hasOverride && (
                  <button onClick={function() { setMonthOverride(i, null) }} style={s.resetBtn}>RESET</button>
                )}
                {!hasOverride && <div style={s.defaultTag}>DEFAULT</div>}
              </div>
            )
          })}
        </div>
      </div>

      {/* Blocked dates */}
      <div style={{ marginTop: '24px' }}>
        <div style={s.settingLabel}>BLOCKED DATES</div>
        <div style={s.settingDesc}>These dates will show as unavailable to store partners. Use for holidays, days off, etc.</div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', marginBottom: '16px' }}>
          <input type="date" value={newBlockDate} onChange={function(e) { setNewBlockDate(e.target.value) }} style={s.input} min={toISO(new Date())} />
          <button onClick={addBlockedDate} style={s.addBtn}>BLOCK DATE</button>
        </div>

        {(!settings.blocked_dates || settings.blocked_dates.length === 0) && <div style={s.empty}>No blocked dates.</div>}

        {(settings.blocked_dates || []).sort().map(function(date) {
          var isPast = date < toISO(new Date())
          return (
            <div key={date} style={{ ...s.blockedRow, opacity: isPast ? 0.4 : 1 }}>
              <span style={s.blockedDate}>{new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
              {isPast && <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>PAST</span>}
              <button onClick={function() { removeBlockedDate(date) }} style={s.removeBtn}>X</button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

var s = {
  container: { padding: '20px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', marginBottom: '24px' },
  sectionTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 600, letterSpacing: '2px', color: '#b48c3c', marginBottom: '16px' },
  saved: { padding: '8px 12px', backgroundColor: 'rgba(74,140,106,0.15)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '3px', color: '#4a8c6a', fontSize: '12px', marginBottom: '12px' },
  settingRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px' },
  settingLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', color: '#ffffff' },
  settingDesc: { fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '4px' },
  counterRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  counterBtn: { width: '36px', height: '36px', backgroundColor: 'rgba(180,140,60,0.15)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', color: '#b48c3c', fontSize: '18px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  counterValue: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '28px', fontWeight: 700, color: '#ffffff', minWidth: '40px', textAlign: 'center' },

  // Monthly overrides
  yearBtn: { padding: '6px 12px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontSize: '16px', cursor: 'pointer' },
  yearLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '18px', fontWeight: 700, color: '#ffffff', minWidth: '60px', textAlign: 'center' },
  monthGrid: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' },
  monthCard: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '12px', textAlign: 'center' },
  monthName: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' },
  monthInputRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' },
  monthBtn: { width: '24px', height: '24px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
  monthValue: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '22px', fontWeight: 700, minWidth: '24px' },
  resetBtn: { marginTop: '6px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '9px', letterSpacing: '1px', cursor: 'pointer', padding: 0 },
  defaultTag: { marginTop: '6px', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '9px', letterSpacing: '1px', color: 'rgba(255,255,255,0.15)' },

  input: { padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '14px', fontFamily: "'Barlow',sans-serif", outline: 'none' },
  addBtn: { padding: '10px 16px', backgroundColor: 'rgba(224,92,92,0.15)', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '3px', color: '#e05c5c', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  empty: { color: 'rgba(255,255,255,0.2)', fontSize: '12px', padding: '8px 0' },
  blockedRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  blockedDate: { color: '#ffffff', fontSize: '13px', flex: 1 },
  removeBtn: { padding: '4px 8px', backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '3px', color: '#e05c5c', fontSize: '12px', cursor: 'pointer' },
}
